import { useState } from "react";

import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Page,
  Panel,
  Skeleton,
  Stack,
  StatusBadge,
  Toolbar,
} from "@/components/ui";

import { PagedTable } from "../components/PagedTable";
import { useHeldRoles, useRevokeAssignment, type RoleAssignment } from "../lib/queries";

/** One account by its subject id, and the role assignments it holds. */
export function Accounts() {
  const [typed, setTyped] = useState("");
  const [subjectId, setSubjectId] = useState<string | undefined>(undefined);
  const roles = useHeldRoles(subjectId);
  const revoke = useRevokeAssignment(subjectId);

  return (
    <Page title="Accounts" eyebrow="Platform">
      <Panel title="Account">
        <Toolbar fill>
          <Field label="Subject id">
            <Input
              mono
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
            />
          </Field>
          <Button
            variant="primary"
            disabled={typed.trim() === ""}
            onClick={() => {
              setSubjectId(typed.trim());
            }}
          >
            Look up
          </Button>
        </Toolbar>
      </Panel>

      <Panel title="Roles held" subtitle={subjectId}>
        {subjectId === undefined ? (
          <EmptyState bare headline="No account opened" />
        ) : roles.isPending ? (
          <Skeleton height={140} />
        ) : roles.isError ? (
          <ErrorState
            headline="Roles unavailable"
            code={roles.error.message}
            onRetry={() => void roles.refetch()}
            retryLabel="Retry"
          />
        ) : (
          <Stack gap={5}>
            <PagedTable
              rows={roles.data}
              caption="Role assignments"
              emptyHeadline="Holds nothing"
              placeholder="Find an assignment"
              density="comfortable"
              match={(row, needle) =>
                `${row.role} ${row.scope.scope} ${row.scope.key ?? ""}`
                  .toLowerCase()
                  .includes(needle)
              }
              columns={[
                { key: "role", header: "Role", emphasis: true, mono: true },
                {
                  key: "scope",
                  header: "Scope",
                  render: (row: RoleAssignment) => (
                    <StatusBadge tone={row.scope.scope === "platform" ? "warning" : "neutral"}>
                      {row.scope.scope}
                    </StatusBadge>
                  ),
                },
                {
                  key: "key",
                  header: "Key",
                  mono: true,
                  muted: true,
                  render: (row: RoleAssignment) => row.scope.key ?? "-",
                },
                { key: "granted_at", header: "Granted", muted: true },
                {
                  key: "granted_by",
                  header: "By",
                  mono: true,
                  muted: true,
                  render: (row: RoleAssignment) => row.granted_by ?? "-",
                },
                {
                  key: "revoke",
                  header: "",
                  align: "right",
                  width: "110px",
                  render: (row: RoleAssignment) => (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        revoke.mutate(row.id);
                      }}
                    >
                      Revoke
                    </Button>
                  ),
                },
              ]}
            />
            {revoke.isError ? <ErrorState headline={revoke.error.message} /> : null}
          </Stack>
        )}
      </Panel>
    </Page>
  );
}
